/**
 * Extract program error names from raw Solana transaction error objects.
 *
 * Four error spaces, resolved in order:
 *   1. Morfi program errors (markets 6000+, cp-swap 6000-6014) — via
 *      @morfi/contract-sdk parseTransactionError, which reads @morfi/errors
 *      (regenerated from the contract `#[error_code]` modules). markets and
 *      cp-swap share part of the 6000+ range; the parser attributes by the
 *      originating program id from the failure log, or by `programIdHint`.
 *   2. SPL Token errors (0–17) — frozen on-chain; names sourced from the
 *      single SPL_TOKEN_ERROR_NAMES list in ./errors.ts so the i18n map and
 *      this parser cannot drift.
 *   3. Anchor framework errors (100–5000) — via @coral-xyz/anchor
 *      LangErrorMessage; returned as `AnchorError_<code>` so the global
 *      funnel can show the generic Anchor message verbatim.
 */

import { LangErrorMessage } from '@coral-xyz/anchor';
import { parseTransactionError } from '@morfi/contract-sdk';
import { SPL_TOKEN_ERROR_NAMES, type SplTokenErrorName } from './errors.js';

const SPL_TOKEN_ERRORS: ReadonlyMap<number, SplTokenErrorName> = new Map(
  SPL_TOKEN_ERROR_NAMES.map((name, index) => [index, name]),
);

export interface ExtractMorfiErrorOptions {
  /**
   * base58 program id used to disambiguate the markets/cp-swap overlap when the
   * raw error doesn't carry a `Program <id> failed` log line (e.g. a confirmed
   * tx's `err` object without logs). Swap hooks should pass the cp-swap id.
   */
  programIdHint?: string;
}

// ALLOW-UNKNOWN: accepts arbitrary Solana RPC errors that have no stable type
export function extractMorfiErrorName(
  solanaError: unknown,
  opts?: ExtractMorfiErrorOptions,
): string | undefined {
  if (solanaError === null || solanaError === undefined || typeof solanaError !== 'object') {
    return undefined;
  }

  const morfiError =
    opts?.programIdHint !== undefined
      ? parseTransactionError(solanaError, { programIdHint: opts.programIdHint })
      : parseTransactionError(solanaError);
  if (morfiError !== null) {
    return morfiError.name;
  }

  const customCode = extractCustomCode(solanaError);
  if (customCode === undefined) {
    return undefined;
  }

  const splError = SPL_TOKEN_ERRORS.get(customCode);
  if (splError !== undefined) {
    return splError;
  }

  if (LangErrorMessage.has(customCode)) {
    return `AnchorError_${String(customCode)}`;
  }

  return undefined;
}

function extractCustomCode(error: object): number | undefined {
  const err = error as Record<string, unknown>;
  const instructionError = err.InstructionError;
  if (!Array.isArray(instructionError) || instructionError.length < 2) {
    return undefined;
  }

  const inner = instructionError[1];
  if (inner === null || inner === undefined || typeof inner !== 'object') {
    return undefined;
  }

  const customCode = (inner as Record<string, unknown>).Custom;
  if (typeof customCode !== 'number') {
    return undefined;
  }

  return customCode;
}
